import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../api/axios";
import { toast } from "sonner";
import BottomTabNav from "../components/BottomTabNav";
import { 
  ArrowLeft, 
  Globe, 
  Star, 
  Clock, 
  Video, 
  MessageCircle,
  Calendar,
  DollarSign,
  MapPin,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  Loader2
} from "lucide-react";

// Language code to name mapping
const LANGUAGE_NAMES = {
  en: { en: "English", es: "Inglés", fr: "Anglais", ar: "الإنجليزية" },
  es: { en: "Spanish", es: "Español", fr: "Espagnol", ar: "الإسبانية" },
  fr: { en: "French", es: "Francés", fr: "Français", ar: "الفرنسية" },
  ar: { en: "Arabic", es: "Árabe", fr: "Arabe", ar: "العربية" }
};

const LANGUAGE_FLAGS = {
  en: "🇺🇸",
  es: "🇪🇸",
  fr: "🇫🇷",
  ar: "🇸🇦"
};

const CALL_METHOD_ICONS = {
  zoom: Video,
  google_meet: Video,
  whatsapp: MessageCircle
};

const CALL_METHOD_LABELS = {
  zoom: "Zoom",
  google_meet: "Google Meet",
  whatsapp: "WhatsApp"
};

const AgentProfilePage = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  
  const [agent, setAgent] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Booking state
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(30);
  const [intakeNotes, setIntakeNotes] = useState("");
  const [isBooking, setIsBooking] = useState(false);
  
  const isRTL = i18n.language === 'ar';
  const primaryLanguage = localStorage.getItem('passportTalk_primaryLanguage') || 'en';
  const isEnglishUser = primaryLanguage === 'en';

  // Check if booking was cancelled
  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      toast.info(t('agents.bookingCancelled'));
    }
  }, [searchParams, t]);

  useEffect(() => {
    if (!isEnglishUser) {
      setLoading(false);
      return;
    }
    
    fetchAgentData();
  }, [agentId, isEnglishUser]);

  const fetchAgentData = async () => {
    try {
      const [agentRes, availRes] = await Promise.all([
        api.get(`/live-agents/${agentId}`),
        api.get(`/live-agents/${agentId}/availability`)
      ]);
      
      setAgent(agentRes.data);
      setAvailability(availRes.data.availability || []);
      
      // Auto-select first available date
      if (availRes.data.availability?.length > 0) {
        setSelectedDate(availRes.data.availability[0].date);
      }
    } catch (err) {
      console.error("Failed to load agent:", err);
      setError(t('agents.loadError'));
      toast.error(t('agents.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const getLanguageName = (langCode) => {
    const names = LANGUAGE_NAMES[langCode];
    return names ? names[i18n.language] || names['en'] : langCode;
  };

  const getPrice = () => {
    if (!agent) return 0;
    const priceMap = { 15: agent.price_15, 30: agent.price_30, 60: agent.price_60 };
    return priceMap[selectedDuration];
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error(t('agents.selectTimeSlot'));
      return;
    }

    setIsBooking(true);
    
    try {
      // Create ISO datetime from selected date and time
      const startTimeUtc = `${selectedDate}T${selectedTime}:00Z`;
      
      const response = await api.post('/bookings', {
        agent_id: agentId,
        start_time_utc: startTimeUtc,
        duration: selectedDuration,
        intake_notes: intakeNotes,
        origin_url: window.location.origin  // Dynamic origin for correct redirects
      });
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error("Booking failed:", err);
      if (err.response?.status === 409) {
        toast.error(t('agents.slotUnavailable'));
        // Refresh availability
        fetchAgentData();
      } else {
        toast.error(t('agents.bookingError'));
      }
    } finally {
      setIsBooking(false);
    }
  };

  // Non-English user restriction
  if (!isEnglishUser) {
    return (
      <div className="min-h-screen pb-24 p-6 bg-gradient-to-b from-slate-50 to-white" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl border-2 border-amber-200 shadow-sm p-8 text-center mt-12">
            <AlertCircle className="w-12 h-12 text-amber-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-travel-navy mb-3">
              {t('agents.englishOnlyTitle')}
            </h2>
            <p className="text-slate-600 mb-6">
              {t('agents.englishOnlyMessage')}
            </p>
            <button
              onClick={() => navigate('/conversation')}
              className="bg-aviation text-white rounded-full px-8 py-3 font-semibold"
            >
              {t('agents.backToTranslator')}
            </button>
          </div>
        </div>
        <BottomTabNav />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen pb-24 p-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-2xl mx-auto text-center py-12">
          <div className="w-16 h-16 border-4 border-aviation border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">{t('common.loading')}</p>
        </div>
        <BottomTabNav />
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="min-h-screen pb-24 p-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/agents')}
            className="flex items-center gap-2 text-slate-600 mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('common.back')}
          </button>
          <div className="bg-white rounded-2xl border-2 border-red-200 p-8 text-center">
            <p className="text-red-600">{error || t('agents.notFound')}</p>
          </div>
        </div>
        <BottomTabNav />
      </div>
    );
  }

  const CallMethodIcon = CALL_METHOD_ICONS[agent.call_method] || Video;
  const selectedDayData = availability.find(d => d.date === selectedDate);

  return (
    <div className="min-h-screen pb-24 p-6 bg-gradient-to-b from-slate-50 to-white" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/agents')}
          className="flex items-center gap-2 text-slate-600 mb-6 hover:text-travel-navy transition-colors"
          data-testid="back-to-agents"
        >
          {isRTL ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          {t('agents.backToAgents')}
        </button>

        {/* Agent Header Card */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex items-start gap-4">
            <img
              src={agent.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.name)}&background=0EA5E9&color=fff`}
              alt={agent.name}
              className="w-20 h-20 rounded-full object-cover border-2 border-slate-100"
            />
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-travel-navy">{agent.name}</h1>
              
              {/* Languages */}
              <div className="flex items-center gap-2 mt-2">
                <Globe className="w-4 h-4 text-slate-400" />
                <div className="flex items-center gap-1">
                  {agent.languages.map((lang) => (
                    <span key={lang} title={getLanguageName(lang)}>
                      {LANGUAGE_FLAGS[lang]}
                    </span>
                  ))}
                </div>
                <span className="text-sm text-slate-600">
                  {agent.languages.map(l => getLanguageName(l)).join(", ")}
                </span>
              </div>
              
              {/* Call Method */}
              <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                <CallMethodIcon className="w-4 h-4" />
                <span>{t('agents.callVia')} {CALL_METHOD_LABELS[agent.call_method]}</span>
              </div>
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-travel-navy mb-3">{t('agents.about')}</h2>
          <p className="text-slate-600 leading-relaxed">{agent.bio}</p>
        </div>

        {/* Expertise Tags */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-travel-navy mb-3">{t('agents.expertise')}</h2>
          <div className="flex flex-wrap gap-2">
            {agent.expertise_tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 bg-aviation/10 text-aviation rounded-full text-sm font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Regions */}
        {agent.regions?.length > 0 && (
          <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-travel-navy mb-3 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              {t('agents.regions')}
            </h2>
            <p className="text-slate-600">{agent.regions.join(", ")}</p>
          </div>
        )}

        {/* Pricing Section */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-travel-navy mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            {t('agents.selectDuration')}
          </h2>
          <div className="grid grid-cols-3 gap-3">
            {[15, 30, 60].map((duration) => {
              const price = { 15: agent.price_15, 30: agent.price_30, 60: agent.price_60 }[duration];
              return (
                <button
                  key={duration}
                  onClick={() => setSelectedDuration(duration)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    selectedDuration === duration
                      ? 'border-aviation bg-aviation/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`duration-${duration}`}
                >
                  <p className="text-lg font-bold text-travel-navy">{duration} {t('agents.min')}</p>
                  <p className="text-sm text-aviation font-semibold">${price}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Availability Calendar */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-travel-navy mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            {t('agents.selectDate')}
          </h2>
          
          {availability.length === 0 ? (
            <p className="text-slate-500 text-center py-4">{t('agents.noAvailability')}</p>
          ) : (
            <>
              {/* Date Selector */}
              <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
                {availability.map((day) => (
                  <button
                    key={day.date}
                    onClick={() => {
                      setSelectedDate(day.date);
                      setSelectedTime(null);
                    }}
                    className={`flex-shrink-0 p-3 rounded-xl border-2 transition-all min-w-[80px] ${
                      selectedDate === day.date
                        ? 'border-aviation bg-aviation/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`date-${day.date}`}
                  >
                    <p className="text-xs text-slate-500">{day.day_name.slice(0, 3)}</p>
                    <p className="text-sm font-semibold text-travel-navy">
                      {new Date(day.date + 'T00:00:00').toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}
                    </p>
                  </button>
                ))}
              </div>

              {/* Time Slots */}
              {selectedDayData && (
                <div>
                  <p className="text-sm text-slate-500 mb-3">{t('agents.availableSlots')} (UTC)</p>
                  <div className="grid grid-cols-4 gap-2">
                    {selectedDayData.slots.map((time) => (
                      <button
                        key={time}
                        onClick={() => setSelectedTime(time)}
                        className={`p-2 rounded-lg border text-sm font-medium transition-all ${
                          selectedTime === time
                            ? 'border-aviation bg-aviation text-white'
                            : 'border-slate-200 text-slate-600 hover:border-aviation hover:text-aviation'
                        }`}
                        data-testid={`time-${time}`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Intake Notes */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-travel-navy mb-3">
            {t('agents.intakeQuestion')}
          </h2>
          <p className="text-sm text-slate-500 mb-3">{t('agents.intakeHint')}</p>
          <textarea
            value={intakeNotes}
            onChange={(e) => setIntakeNotes(e.target.value)}
            placeholder={t('agents.intakePlaceholder')}
            className="w-full h-24 bg-slate-50 border-2 border-slate-200 focus:border-aviation focus:ring-4 focus:ring-aviation/10 rounded-xl p-4 resize-none text-sm"
            data-testid="intake-notes"
          />
        </div>

        {/* Legal Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <p className="text-xs text-amber-800">
            {t('agents.legalDisclaimer')}
          </p>
        </div>

        {/* Booking Summary & Button */}
        <div className="bg-white rounded-2xl border-2 border-aviation shadow-sm p-6 sticky bottom-24">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-slate-500">{t('agents.total')}</p>
              <p className="text-2xl font-bold text-aviation">${getPrice()}</p>
            </div>
            <div className="text-right">
              {selectedDate && selectedTime && (
                <>
                  <p className="text-sm text-slate-500">{selectedDate}</p>
                  <p className="text-sm font-semibold text-travel-navy">{selectedTime} UTC • {selectedDuration} min</p>
                </>
              )}
            </div>
          </div>
          
          <button
            onClick={handleBooking}
            disabled={!selectedDate || !selectedTime || isBooking}
            className="w-full bg-gradient-to-r from-aviation to-sky-500 text-white rounded-full h-14 font-semibold text-lg transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="confirm-booking-btn"
          >
            {isBooking ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('agents.processing')}
              </>
            ) : (
              <>
                {t('agents.proceedToPayment')}
              </>
            )}
          </button>
          
          <p className="text-xs text-center text-slate-500 mt-3">
            {t('agents.stripeSecure')}
          </p>
        </div>
      </div>

      <BottomTabNav />
    </div>
  );
};

export default AgentProfilePage;
